import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useEditorViewStore, useModelStore } from '@editorStores'
import { getPlans, getResultsValid } from '@queries'
import { postCreateLoadPlan, postCreatePositionPlan, postCreateStiffeningPlan } from '@mutations'
import { usePlanPdfViewer } from '../PlanPdfViewerContext'
import { PlanViewPlanChoice } from '../constants'
import downloadPlanPdf from '../downloadPlanPdf'
import { getTodayHandInDate } from '../utils'

/** Name, comment and tags the generated plan is saved with. */
export interface NewPlanMetadata {
  name: string
  comment: string
  tags: string[]
}

/**
 * Generates the plan PDF for the previewed storeys/plan type, saved under the given name, comment
 * and tags, and once ready highlights it in the "Pläne" tab and (with "Plan herunterladen" on)
 * downloads it. `save_plan` always appends a new row rather than overwriting, and the create
 * endpoints answer with the full (unordered) plan list, so the just-created plan is found by
 * diffing the response against a snapshot taken right before the request. `hasResults`: the
 * project has (at least partially) valid results, which the plan is built from.
 */
const useGeneratePlan = (storeys: readonly string[], planType: PlanViewPlanChoice) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const setSelectedPlanGuid = useEditorViewStore(state => state.setPlanViewSelectedPlanGuid)
  const downloadAfterGenerate = useEditorViewStore(state => state.planView.downloadAfterGenerate)
  const { changedDocument } = usePlanPdfViewer()
  const projectName = useModelStore(state => state.project?.name)

  const { data: resultsValid } = useQuery({
    queryKey: getResultsValid.getKey(projectId ?? ''),
    queryFn: () => getResultsValid.request(projectId!),
    enabled: Boolean(projectId),
    refetchInterval: 5000,
  })
  const hasResults = Boolean(resultsValid?.fully_valid || resultsValid?.partially_valid)

  const downloadCreatedPlan = async (plan: Plan) => {
    try {
      await downloadPlanPdf(plan, projectName)
    } catch {
      enqueueSnackbar('Fehler beim Herunterladen des Plans', { variant: 'error' })
    }
  }

  const { isLoading, mutateAsync } = useMutation(
    async (metadata: NewPlanMetadata) => {
      const id = projectId as string
      const previousPlans: Plan[] = await getPlans.request(id)
      const previousGuids = new Set(previousPlans.map(plan => plan.guid))
      const params = new URLSearchParams({
        add_header: 'true',
        hand_in_date: getTodayHandInDate(),
        plan_name: metadata.name,
        plan_comment: metadata.comment,
      })
      metadata.tags.forEach(tag => params.append('plan_tags', tag))
      // The stiffening plan always previews exactly one storey.
      if (planType === 'stiffeningPlan') params.set('storey', storeys[0])
      const plans: Plan[] =
        planType === 'loadPlan'
          ? // The load plan always covers the whole building.
            await postCreateLoadPlan.request(id, params)
          : planType === 'positionPlan'
          ? await postCreatePositionPlan.request(id, [...storeys], params)
          : await postCreateStiffeningPlan.request(id, params)
      return { plans, createdPlan: plans.find(plan => !previousGuids.has(plan.guid)) }
    },
    {
      onSuccess: ({ plans, createdPlan }) => {
        // The create endpoints answer with the full plan list, so the "Pläne" tab updates at once.
        const plansKey = getPlans.getKey(projectId as string)
        queryClient.setQueryData(plansKey, plans)
        queryClient.invalidateQueries(plansKey)
        enqueueSnackbar('Plan wurde erfolgreich erstellt', { variant: 'success' })
        if (!createdPlan) return
        // Generating takes a while: meanwhile the user may have edited another plan's PDF in the
        // "Pläne" tab. Switching the preview would drop those edits (and a later save would store
        // the new plan's PDF under the edited plan), so the highlight then stays put.
        if (!changedDocument) setSelectedPlanGuid(createdPlan.guid)
        if (!downloadAfterGenerate) return
        // Not awaited: the mutation (and its loading state) ends once the plan is created.
        void downloadCreatedPlan(createdPlan)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Erstellen des Plans'), {
          variant: 'error',
        })
      },
    },
  )

  /** Generates the plan; resolves whether it was created. */
  const generatePlan = async (metadata: NewPlanMetadata): Promise<boolean> => {
    try {
      await mutateAsync(metadata)
      return true
    } catch {
      // Reported by onError.
      return false
    }
  }

  return { generatePlan, isGenerating: isLoading, hasResults }
}

export default useGeneratePlan
